// The product's driver: an adult racer at real proportions (1.78 m), always in
// the helmet, in a race suit painted from the car's livery (side panels,
// collar, belt, piping, number on the chest, the product's name on the back).
// Customizable per product: helmet colors, helmet design, visor tint.
// Faces +z, standing on y = 0.
import * as THREE from 'three'

export type Driver = {
  visor: string
  helmet_pattern: 'stripe' | 'halves' | 'chevron' | 'solid' | string
  helmet_primary: string
  helmet_secondary: string
  suit: string
  suit_secondary: string
  suit_accent: string
  number: number | string
  name?: string
  [key: string]: unknown
}

const FONT = '"Saira Condensed", "Arial Narrow", sans-serif'

function texture(w: number, h: number, draw: (x: CanvasRenderingContext2D) => void) {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  draw(canvas.getContext('2d')!)
  const t = new THREE.CanvasTexture(canvas)
  t.colorSpace = THREE.SRGBColorSpace
  t.anisotropy = 8
  return t
}

function contrast(hex: string) {
  const n = parseInt(hex.replace('#', ''), 16)
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) > 150 ? '#141416' : '#F7F7F5'
}

// Torso texture on a lathe: u = 0 front, 0.25 right side, 0.5 back, 0.75 left;
// v = 0 at the hips, 1 at the collar (canvas y is flipped).
function suitTexture(d: Driver) {
  const W = 1024
  const H = 512
  return texture(W, H, (x) => {
    x.fillStyle = d.suit
    x.fillRect(0, 0, W, H)
    // Side panels from the hips to the armpits, with accent piping.
    for (const u of [0.25, 0.75]) {
      x.fillStyle = d.suit_secondary
      x.fillRect(W * (u - 0.07), H * 0.18, W * 0.14, H * 0.82)
      x.fillStyle = d.suit_accent
      x.fillRect(W * (u - 0.075), H * 0.18, W * 0.008, H * 0.82)
      x.fillRect(W * (u + 0.067), H * 0.18, W * 0.008, H * 0.82)
    }
    // Shoulder yoke and collar.
    x.fillStyle = d.suit_secondary
    x.fillRect(0, 0, W, H * 0.14)
    x.fillStyle = d.suit_accent
    x.fillRect(0, H * 0.14, W, H * 0.015)
    // Belt band at the waist.
    x.fillStyle = d.suit_secondary
    x.fillRect(0, H * 0.82, W, H * 0.06)
    // Zip down the front (u = 0 wraps to both edges).
    x.fillStyle = 'rgba(0,0,0,0.35)'
    x.fillRect(0, H * 0.14, W * 0.004, H * 0.68)
    x.fillRect(W - W * 0.004, H * 0.14, W * 0.004, H * 0.68)
    // Number on the chest (left of the zip as worn).
    x.fillStyle = '#F7F7F5'
    x.beginPath()
    x.roundRect(W * 0.03, H * 0.22, W * 0.07, H * 0.16, 10)
    x.fill()
    x.fillStyle = '#141416'
    x.font = `italic 800 ${H * 0.13}px ${FONT}`
    x.textAlign = 'center'
    x.textBaseline = 'middle'
    x.fillText(String(d.number), W * 0.065, H * 0.305)
    // The product's name across the back.
    x.fillStyle = contrast(d.suit)
    x.font = `italic 800 ${H * 0.14}px ${FONT}`
    x.fillText(String(d.name ?? '').toUpperCase(), W * 0.5, H * 0.34, W * 0.2)
  })
}

function helmetTexture(d: Driver) {
  const w = 1024
  const h = 512
  // Sphere UVs: u = 0.25 front (+z), 0.75 back; canvas y = 0 at the crown.
  return texture(w, h, (x) => {
    x.fillStyle = d.helmet_primary
    x.fillRect(0, 0, w, h)
    x.fillStyle = d.helmet_secondary
    switch (d.helmet_pattern) {
      case 'stripe':
        x.fillRect(w * 0.21, 0, w * 0.08, h * 0.5)
        x.fillRect(w * 0.71, 0, w * 0.08, h * 0.62)
        x.fillRect(0, h * 0.6, w, h * 0.05)
        break
      case 'halves':
        x.fillRect(0, h * 0.42, w, h * 0.58)
        break
      case 'chevron':
        for (const [cx, sign] of [[0.25, 1], [0.75, 1]] as const) {
          x.beginPath()
          x.moveTo(w * (cx - 0.16), h * 0.62)
          x.lineTo(w * cx, h * 0.22 * sign)
          x.lineTo(w * (cx + 0.16), h * 0.62)
          x.lineTo(w * (cx + 0.1), h * 0.62)
          x.lineTo(w * cx, h * 0.38)
          x.lineTo(w * (cx - 0.1), h * 0.62)
          x.closePath()
          x.fill()
        }
        break
    }
    // Sponsor-style band and the number on the back.
    x.fillStyle = d.suit_accent
    x.fillRect(0, h * 0.68, w, h * 0.02)
    x.fillStyle = contrast(d.helmet_primary)
    x.font = `italic 800 ${h * 0.16}px ${FONT}`
    x.textAlign = 'center'
    x.textBaseline = 'middle'
    x.fillText(String(d.number), w * 0.75, h * 0.48)
  })
}

// A racing helmet (radius ~ 0.14 m): shell, visor, chin bar, neck roll, top spoiler.
export function buildHelmet(d: Driver, radius = 0.14): THREE.Group {
  const g = new THREE.Group()
  const shellMat = new THREE.MeshPhysicalMaterial({ map: helmetTexture(d), roughness: 0.22, clearcoat: 1, clearcoatRoughness: 0.04 })
  const shell = new THREE.Mesh(new THREE.SphereGeometry(radius, 48, 32), shellMat)
  shell.scale.set(0.94, 1.0, 1.1)
  shell.castShadow = true
  g.add(shell)
  // Visor: a band of the shell, slightly proud, tinted.
  const visor = new THREE.Mesh(
    new THREE.SphereGeometry(radius * 1.012, 48, 16, Math.PI / 2 - 0.78, 1.56, Math.PI * 0.4, Math.PI * 0.17),
    new THREE.MeshPhysicalMaterial({ color: d.visor, roughness: 0.03, metalness: 0.75, clearcoat: 1 }),
  )
  visor.scale.copy(shell.scale)
  g.add(visor)
  // Visor seal, chin bar, neck roll.
  const trim = new THREE.MeshStandardMaterial({ color: 0x111214, roughness: 0.5 })
  const seal = new THREE.Mesh(new THREE.TorusGeometry(radius * 0.99, radius * 0.025, 6, 48, 1.7), trim)
  seal.rotation.set(Math.PI * 0.07, Math.PI / 2 - 0.85, 0)
  seal.scale.set(0.94, 1, 1.1)
  g.add(seal)
  const chin = new THREE.Mesh(new THREE.SphereGeometry(radius * 0.62, 24, 12, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.3), shellMat)
  chin.position.set(0, -radius * 0.42, radius * 0.38)
  chin.scale.set(1.05, 1, 1)
  g.add(chin)
  const neckRoll = new THREE.Mesh(new THREE.TorusGeometry(radius * 0.6, radius * 0.12, 8, 24), trim)
  neckRoll.rotation.x = Math.PI / 2
  neckRoll.position.y = -radius * 0.82
  g.add(neckRoll)
  const spoiler = new THREE.Mesh(new THREE.BoxGeometry(radius * 0.9, radius * 0.07, radius * 0.35), shellMat)
  spoiler.position.set(0, radius * 0.72, -radius * 0.88)
  spoiler.rotation.x = 0.5
  g.add(spoiler)
  return g
}

// A tapered limb segment with rounded ends.
function limb(a: THREE.Vector3, b: THREE.Vector3, ra: number, rb: number, material: THREE.Material): THREE.Group {
  const g = new THREE.Group()
  const len = a.distanceTo(b)
  const body = new THREE.Mesh(new THREE.CylinderGeometry(rb, ra, len, 20, 1, true), material)
  body.position.copy(a).lerp(b, 0.5)
  body.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize())
  for (const [p, r] of [[a, ra], [b, rb]] as const) {
    const cap = new THREE.Mesh(new THREE.SphereGeometry(r, 20, 14), material)
    cap.position.copy(p)
    g.add(cap)
  }
  g.add(body)
  g.traverse((o) => ((o as THREE.Mesh).castShadow = true))
  return g
}

export function buildDriver(d: Driver): THREE.Group {
  const root = new THREE.Group()
  const suit = new THREE.MeshStandardMaterial({ color: d.suit, roughness: 0.62 })
  const suit2 = new THREE.MeshStandardMaterial({ color: d.suit_secondary, roughness: 0.62 })
  const accent = new THREE.MeshStandardMaterial({ color: d.suit_accent, roughness: 0.5 })
  const black = new THREE.MeshStandardMaterial({ color: 0x141416, roughness: 0.7 })
  const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z)

  // Torso: a lathe from the hips to the collar, flattened front to back.
  const profile: [number, number][] = [[0.0, 0.84], [0.14, 0.85], [0.178, 0.91], [0.18, 0.99], [0.164, 1.08], [0.172, 1.18], [0.196, 1.28], [0.21, 1.36], [0.2, 1.42], [0.14, 1.475], [0.07, 1.5], [0.0, 1.505]]
  const torsoGeo = new THREE.LatheGeometry(profile.map(([r, y]) => new THREE.Vector2(r, y)), 48)
  const torso = new THREE.Mesh(torsoGeo, new THREE.MeshStandardMaterial({ map: suitTexture(d), roughness: 0.6 }))
  torso.scale.set(1, 1, 0.62)
  torso.castShadow = true
  root.add(torso)

  // Neck in the balaclava, and the helmet (always on).
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.052, 0.06, 0.1, 16), black)
  neck.position.y = 1.54
  root.add(neck)
  const helmet = buildHelmet(d, 0.155)
  helmet.position.set(0, 1.665, 0.015)
  root.add(helmet)

  // Legs: thighs, shins, boots, in a relaxed stance.
  for (const s of [-1, 1]) {
    root.add(limb(V(s * 0.1, 0.9, 0), V(s * 0.115, 0.5, 0.015), 0.095, 0.066, suit))
    root.add(limb(V(s * 0.115, 0.5, 0.015), V(s * 0.125, 0.13, -0.01), 0.064, 0.048, suit))
    const knee = new THREE.Mesh(new THREE.CylinderGeometry(0.068, 0.068, 0.05, 16), suit2)
    knee.position.set(s * 0.115, 0.5, 0.015)
    root.add(knee)
    const boot = new THREE.Mesh(new THREE.CapsuleGeometry(0.052, 0.16, 6, 14), suit2)
    boot.rotation.x = Math.PI / 2
    boot.scale.set(1.08, 1, 0.9)
    boot.position.set(s * 0.125, 0.055, 0.05)
    boot.castShadow = true
    const sole = new THREE.Mesh(new THREE.BoxGeometry(0.105, 0.022, 0.28), accent)
    sole.position.set(s * 0.125, 0.011, 0.05)
    const cuff = new THREE.Mesh(new THREE.CylinderGeometry(0.054, 0.05, 0.07, 16), suit2)
    cuff.position.set(s * 0.125, 0.14, -0.01)
    root.add(boot, sole, cuff)
  }

  // Arms: relaxed, hands by the thighs; shoulder caps and cuffs in the
  // second color, gloves in the second color with accent cuffs.
  for (const s of [-1, 1]) {
    const shoulder = V(s * 0.225, 1.41, 0)
    const elbow = V(s * 0.29, 1.14, -0.035)
    const wrist = V(s * 0.3, 0.9, 0.03)
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.072, 18, 12), suit2)
    cap.position.copy(shoulder)
    cap.scale.set(1.05, 0.9, 1)
    root.add(cap)
    root.add(limb(shoulder, elbow, 0.062, 0.05, suit))
    root.add(limb(elbow, wrist, 0.049, 0.039, suit))
    const elbowPad = new THREE.Mesh(new THREE.CylinderGeometry(0.052, 0.052, 0.045, 14), suit2)
    elbowPad.position.copy(elbow)
    elbowPad.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), wrist.clone().sub(elbow).normalize())
    root.add(elbowPad)
    const gloveCuff = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.042, 0.05, 14), accent)
    gloveCuff.position.copy(wrist)
    const glove = new THREE.Mesh(new THREE.SphereGeometry(0.048, 16, 12), suit2)
    glove.scale.set(0.72, 1.4, 1.05)
    glove.position.set(s * 0.302, 0.84, 0.035)
    glove.castShadow = true
    const thumb = new THREE.Mesh(new THREE.CapsuleGeometry(0.015, 0.035, 4, 8), suit2)
    thumb.position.set(s * 0.29, 0.86, 0.075)
    thumb.rotation.x = 0.6
    root.add(glove, gloveCuff, thumb)
  }
  return root
}
